/**
 * "Sign in SIED" (T-100 phase 2) — shapes returned by the NestJS endpoints and
 * the EN/FR strings for the sidebar row and the confirmation dialog.
 *
 * Eligibility is computed server-side (packages/nestjs/src/sied/
 * sied-sign-eligibility.ts) and returned with the state, so the button and the
 * server can't disagree.
 */

export type Lang = 'en' | 'fr';

export type SignState =
  | 'hidden'
  | 'signed'
  | 'running'
  | 'blocked'
  | 'not_yet'
  | 'ready';

export interface SignReason {
  code: string;
  en: string;
  fr: string;
}

export interface SignEligibility {
  state: SignState;
  reasons: SignReason[];
  availableFrom: string | null;
  availableFromSource: 'cremation_date' | 'status_change_event' | null;
}

export interface SignRun {
  status: 'running' | 'saved_only' | 'signed' | 'failed' | null;
  progress: string | null;
  startedAt: string | null;
  requestedByName: string | null;
  signedAt: string | null;
  signerName: string | null;
  signedByName: string | null;
  enteredValues: Record<string, unknown> | null;
  error: string | null;
}

export interface SignStateResponse {
  funeralId: number;
  migrationReady: boolean;
  eligibility: SignEligibility;
  sp3Number: string | null;
  formNumber: string | null;
  siedId: string | null;
  siedStatus: string | null;
  siedStatusRaw: string | null;
  transportDate: string | null;
  sign: SignRun | null;
  proofUrl: string | null;
}

export interface IdentityFacts {
  formNumber: string | null;
  lastName: string | null;
  firstName: string | null;
  dateOfDeath: string | null;
  /** Cleo only: clients.maiden_name — SIED often shows it (Québec birth surname). */
  maidenName?: string | null;
}

export interface SignerName {
  first: string;
  last: string;
}

export interface SignPreview {
  eligibility: SignEligibility;
  cleo: IdentityFacts;
  sied:
    | (IdentityFacts & { statusRaw: string; status: string; siedId: string })
    | null;
  mismatches: Array<{
    field: 'formNumber' | 'lastName' | 'firstName' | 'dateOfDeath';
    cleo: string | null;
    sied: string | null;
  }>;
  alreadySigned: boolean;
  current: {
    nom: string;
    prenom: string;
    noSite: string;
    datePriseEnCharge: string;
    dispositions: string[];
    prerequisites: string[];
  } | null;
  toEnter: {
    signers: SignerName[];
    siteId: string;
    siteLabel: string;
    date: string | null;
    mode: 'C';
  };
  canProceed: boolean;
  problems: string[];
}

export const SIED_BASE_URL = 'https://sied.apps.ti.sante.quebec';

export const DEFAULT_SIGNER: SignerName = { first: 'Bram', last: 'Paperman' };

export const signerLabel = (s: SignerName) => `${s.first} ${s.last}`;

/** Bram Paperman when on the list, else the first signer. */
export function defaultSigner(list: SignerName[]): SignerName | null {
  return (
    list.find(
      (s) => s.first === DEFAULT_SIGNER.first && s.last === DEFAULT_SIGNER.last
    ) ??
    list[0] ??
    null
  );
}

export function formatWhen(iso: string | null | undefined, lang: Lang): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    timeZone: 'America/Toronto',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const PROGRESS_LABELS: Record<string, Record<Lang, string>> = {
  queued: { en: 'Starting…', fr: 'Démarrage…' },
  login: { en: 'Logging in to SIED…', fr: 'Connexion à SIED…' },
  search: { en: 'Finding the form…', fr: 'Recherche du formulaire…' },
  form_found: {
    en: 'Form found · checking the name and date',
    fr: 'Formulaire trouvé · vérification du nom et de la date',
  },
  saving: {
    en: 'Saving Disposition du corps…',
    fr: 'Enregistrement de la disposition du corps…',
  },
  saved: {
    en: 'Disposition saved and re-read: matches',
    fr: 'Disposition enregistrée et relue : conforme',
  },
  signing: { en: 'Signing…', fr: 'Signature…' },
  done: { en: 'Done', fr: 'Terminé' },
};

const L = <T extends Record<string, Record<Lang, string>>>(x: T) => x;

export const SIGN_LABELS = L({
  button: { en: 'Sign in SIED', fr: 'Signer dans SIED' },
  retry: { en: 'Retry', fr: 'Réessayer' },
  openInSied: { en: 'Open in SIED', fr: 'Ouvrir dans SIED' },
  availableFrom: { en: 'Available from', fr: 'Disponible à partir du' },
  signing: { en: 'Signing in SIED…', fr: 'Signature dans SIED…' },
  startedBy: { en: 'Started by', fr: 'Lancé par' },
  lockedNote: {
    en: 'Other staff see this too; the button is locked.',
    fr: 'Visible par toute l’équipe; le bouton est verrouillé.',
  },
  signedBy: { en: 'Signed by', fr: 'Signé par' },
  signedInSied: { en: 'Signed in SIED', fr: 'Signé dans SIED' },
  clickedBy: { en: 'Clicked by', fr: 'Lancé par' },
  viewProof: { en: 'View SIED proof', fr: 'Voir la preuve SIED' },
  failed: { en: 'Sign failed', fr: 'Échec de la signature' },
  savedOnly: {
    en: 'Saved in SIED, not signed',
    fr: 'Enregistré dans SIED, non signé',
  },
  dialogTitle: {
    en: 'Sign the SIED form for',
    fr: 'Signer le formulaire SIED de',
  },
  dialogSub: {
    en: 'You can’t undo signing in SIED. Check each line.',
    fr: 'Une signature dans SIED est irréversible. Vérifiez chaque ligne.',
  },
  reading: {
    en: 'Reading the form in SIED (about 15 s)…',
    fr: 'Lecture du formulaire dans SIED (environ 15 s)…',
  },
  previewFailed: {
    en: 'Could not read SIED',
    fr: 'Lecture de SIED impossible',
  },
  field: { en: 'Field', fr: 'Champ' },
  cleoRecord: { en: 'Cleo record', fr: 'Dossier Cleo' },
  siedNow: { en: 'SIED form now', fr: 'Formulaire SIED' },
  formNo: { en: 'SP-3 form no.', fr: 'No de formulaire SP-3' },
  deceased: { en: 'Deceased', fr: 'Défunt' },
  dateOfDeath: { en: 'Date of death', fr: 'Date du décès' },
  siedStatus: { en: 'SIED status', fr: 'Statut SIED' },
  match: { en: 'match', fr: 'conforme' },
  mismatch: { en: 'differs', fr: 'différent' },
  ourTurn: { en: 'our turn', fr: 'à nous' },
  willEnter: {
    en: 'What we’ll enter on “Disposition du corps”',
    fr: 'Ce que nous saisirons dans « Disposition du corps »',
  },
  representative: {
    en: 'Representative (signs legally)',
    fr: 'Représentant (signataire légal)',
  },
  fromSettings: { en: 'from Settings', fr: 'des paramètres' },
  site: { en: 'Site funéraire', fr: 'Site funéraire' },
  fixed: { en: 'fixed', fr: 'fixe' },
  datePec: { en: 'Date de prise en charge', fr: 'Date de prise en charge' },
  transportDispatched: { en: 'transport dispatched', fr: 'transport' },
  mode: { en: 'Mode de disposition', fr: 'Mode de disposition' },
  cremation: { en: 'Crémation', fr: 'Crémation' },
  permit: { en: 'Permit, address', fr: 'Permis, adresse' },
  leftAsIs: { en: 'left as SIED has them', fr: 'laissés tels quels dans SIED' },
  notTouched: { en: 'not touched', fr: 'inchangés' },
  siedHasNow: { en: 'SIED has now', fr: 'SIED contient' },
  step1: {
    en: '1. Fill and save (reversible). We save the tab, reload it and check every value above, and that SIED’s “Prérequis pour la signature” box is gone. Any difference stops the run here.',
    fr: '1. Remplir et enregistrer (réversible). Nous enregistrons l’onglet, le relisons et vérifions chaque valeur ci-dessus, et que l’encadré « Prérequis pour la signature » a disparu. Toute différence arrête ici.',
  },
  step2: {
    en: '2. Sign (irreversible). We tick the engagement box and click Signer, confirm SIED shows “Disposition du corps signée par …” and save a screenshot.',
    fr: '2. Signer (irréversible). Nous cochons l’engagement et cliquons sur Signer, vérifions que SIED affiche « Disposition du corps signée par … » et enregistrons une capture.',
  },
  cantSign: { en: 'Can’t sign', fr: 'Signature impossible' },
  alreadySigned: {
    en: 'SIED already shows this form as signed.',
    fr: 'SIED indique déjà que ce formulaire est signé.',
  },
  mismatchHelp: {
    en: 'Fix it in Cleo or check the SP-3 number. If SIED is wrong, correct it there first.',
    fr: 'Corrigez-le dans Cleo ou vérifiez le numéro SP-3. Si SIED a tort, corrigez-le d’abord dans SIED.',
  },
  confirm: {
    en: 'I confirm this is the right person and that {signer} took charge of the body on {date} for cremation.',
    fr: 'Je confirme qu’il s’agit de la bonne personne et que {signer} a pris en charge le corps le {date} pour crémation.',
  },
  cancel: { en: 'Cancel', fr: 'Annuler' },
  saveOnly: {
    en: 'Save only (don’t sign)',
    fr: 'Enregistrer seulement (sans signer)',
  },
  saveOnlyHelp: {
    en: '“Save only” runs step 1 and stops — for a first live test, or to check the form in SIED by hand before signing.',
    fr: '« Enregistrer seulement » fait l’étape 1 et s’arrête — pour un premier test, ou pour vérifier le formulaire dans SIED avant de signer.',
  },
  startFailed: { en: 'Could not start', fr: 'Démarrage impossible' },
  signers: { en: 'SIED signers', fr: 'Signataires SIED' },
  signersHelp: {
    en: 'Names offered as the legal representative in “Sign in SIED”. SIED credits the signature to this name, not to the login.',
    fr: 'Noms proposés comme représentant légal dans « Signer dans SIED ». SIED attribue la signature à ce nom, pas au compte de connexion.',
  },
});

export const fill = (s: string, vars: Record<string, string>) =>
  s.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');

// ─── Compact sidebar row (one pill + one small action) ──────────────────────

export type SidebarAction = 'sign' | 'retry' | 'details' | null;

export interface SidebarPill {
  key: string;
  label: string;
  /** Tailwind classes, same family as the other sidebar Status pills. */
  cls: string;
  spinner?: boolean;
  action: SidebarAction;
  /** Native tooltip: blocked reasons, SIED's FR text, checked time. */
  tip: string;
}

const PILL_CLS = {
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  violet: 'bg-violet-100 text-violet-800',
  grey: 'bg-gray-100 text-gray-600',
} as const;

export const PILL_LABELS = L({
  ourTurn: { en: 'Our turn', fr: 'À signer' },
  signed: { en: 'Signed', fr: 'Signé' },
  doctor: { en: 'Doctor not signed', fr: 'Médecin non signé' },
  missing: { en: 'Missing info', fr: 'Info manquante' },
  available: { en: 'Available', fr: 'Dès le' },
  failed: { en: 'Failed', fr: 'Échec' },
  saved: { en: 'Saved', fr: 'Enregistré' },
  signing: { en: 'Signing…', fr: 'Signature…' },
  cancelled: { en: 'Cancelled', fr: 'Annulé' },
  noForm: { en: 'No form', fr: 'Aucun formulaire' },
  unknown: { en: 'Unknown', fr: 'Inconnu' },
  sign: { en: 'Sign', fr: 'Signer' },
  retry: { en: 'Retry', fr: 'Réessayer' },
  details: { en: 'SIED signature details', fr: 'Détails de la signature SIED' },
});

const shortDay = (iso: string, lang: Lang) =>
  new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    timeZone: 'America/Toronto',
    month: 'short',
    day: 'numeric',
  });

/**
 * One pill + at most one action for the sidebar SIED row. When / who / proof
 * live in the case feed and the details modal, not inline.
 */
const T_AVAIL: Record<Lang, string> = {
  en: 'Signing opens 24 h after the cremation was scheduled:',
  fr: 'Signature possible 24 h après la planification de la crémation :',
};

export function sidebarPill(
  state: SignStateResponse | null,
  sied: { status?: string | null; raw?: string | null; checkedLine?: string },
  lang: Lang
): SidebarPill {
  const base = [sied.raw ? `SIED : ${sied.raw}` : '', sied.checkedLine ?? '']
    .filter(Boolean)
    .join('\n');
  const tip = (...lines: string[]) =>
    [...lines, base].filter(Boolean).join('\n');
  const p = (
    key: keyof typeof PILL_LABELS,
    cls: keyof typeof PILL_CLS,
    action: SidebarAction,
    t: string,
    extra: Partial<SidebarPill> = {}
  ): SidebarPill => ({
    key,
    label: PILL_LABELS[key][lang],
    cls: PILL_CLS[cls],
    action,
    tip: t,
    ...extra,
  });

  const status = sied.status ?? state?.siedStatus ?? null;
  if (status === 'cancelled') return p('cancelled', 'grey', null, tip());

  // Signing not set up (migration missing / request failed): status only.
  if (!state || !state.migrationReady) {
    if (status === 'funeral_rep_signed')
      return p('signed', 'green', null, tip());
    if (status === 'not_signed_by_physician')
      return p('doctor', 'amber', null, tip());
    if (
      status === 'certifier_signed_not_validated' ||
      status === 'certifier_signed_validated'
    ) {
      return p('ourTurn', 'blue', null, tip());
    }
    if (status === 'unknown') return p('unknown', 'grey', null, tip());
    return p(
      'noForm',
      'grey',
      null,
      tip(
        lang === 'fr' ? 'Aucun formulaire SIED lié' : 'No SIED form linked yet'
      )
    );
  }

  const e = state.eligibility;
  const run = state.sign;
  switch (e.state) {
    case 'signed':
      return p('signed', 'green', 'details', tip());
    case 'running':
      return p('signing', 'blue', null, tip(), { spinner: true });
    case 'not_yet': {
      const when = e.availableFrom ? shortDay(e.availableFrom, lang) : '';
      return {
        ...p(
          'available',
          'grey',
          null,
          tip(
            e.availableFrom
              ? `${T_AVAIL[lang]} ${formatWhen(e.availableFrom, lang)}`
              : ''
          )
        ),
        label: `${PILL_LABELS.available[lang]} ${when}`.trim(),
      };
    }
    case 'blocked': {
      const reasons = e.reasons.map((r) => r[lang]);
      if (e.reasons.some((r) => r.code === 'not_tracked'))
        return p('noForm', 'grey', null, tip(...reasons));
      if (e.reasons.some((r) => r.code === 'doctor_not_signed'))
        return p('doctor', 'amber', null, tip(...reasons));
      return p('missing', 'amber', null, tip(...reasons));
    }
    case 'ready':
      if (run?.status === 'failed')
        return p('failed', 'red', 'retry', tip(run.error ?? ''));
      if (run?.status === 'saved_only')
        return p('saved', 'violet', 'sign', tip());
      return p('ourTurn', 'blue', 'sign', tip());
    default:
      // hidden (not Québec / no form)
      return p('noForm', 'grey', null, tip(...e.reasons.map((r) => r[lang])));
  }
}
